import apiClient, { ApiError, logErrorResponseData, logResponseData } from './apiClient';
import { resolvePurchasedProductPriceId } from '@/lib/resolvePurchasedProductPriceId';
import {
	coercePurchaseTypeEnum,
	normalizeApiProductType,
	normalizeGetProductDetailPayload,
	normalizeGetProductListPayload,
	normalizeGetProductPriceListPayload,
} from '@/utils/productApiNormalize';
import {
	normalizeCreateProductPayload,
	normalizeUpdateProductPayload,
} from '@/utils/productRequestNormalize';
import {
	isContentLikeProductType,
	isInstitutionClassProductSelectType,
} from '@/utils/productUtils';
import type {
    CreateProductRequestDto,
    CreateProductResponseDto,
    GetProductListResponseDto,
    GetPurchasedProductListResponseDto,
    PurchasedProductItemDto,
    ProductListItemDto,
    GetProductDetailResponseDto,
    GetProductItemListResponseDto,
    GetProductPriceListResponseDto,
    ProductItemDetailDto,
    ProductItemListRowDto,
    ProductPriceDetailDto,
    ProductPriceListItemDto,
    ProductPriceCurrency,
    ProductPricePurchaseType,
    ProductType,
    UpdateProductRequestDto,
    UpdateProductResponseDto,
    DeleteProductResponseDto,
    CreateProductPriceRequestDto,
    CreateProductPriceResponseDto,
    DeleteProductPriceResponseDto,
} from '@/types/api/product';

const MAX_PRODUCT_PRICE_LIST_PAGE_SIZE = 100;
/** OpenAPI: GET /product/organization/purchased 에 pageSize 필수 */
const PURCHASED_PRODUCT_PAGE_SIZE = 200;

/** apiClient가 검증 상세까지 합친 `ApiError.message`를 사용 (본문의 짧은 message만 쓰면 상세 유실) */
function extractMessage(error: unknown): string {
    if (error instanceof ApiError) {
        return error.message;
    }
    if (error instanceof Error) return error.message;
    return '알 수 없는 오류가 발생했습니다.';
}

/**
 * GET 프로덕트 목록 — **`GET /product/list`** (`page`, `pageSize`, 선택 **`language`** 쿼리).
 */
export const getProductList = async (params: {
    page: number;
    pageSize: number;
    language?: string;
}): Promise<GetProductListResponseDto> => {
    const path = `/product/list`;
    const queryParams = {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.language ? { language: params.language } : {}),
    };
    try {
        const raw = await apiClient.get<unknown>(path, {
            params: queryParams,
        });
        const normalized = normalizeGetProductListPayload(raw);
        return normalized;
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
            const msg = [
                `GET ${path} 가 404입니다. (연결된 API에 이 라우트가 없거나 i18n API가 아직 배포되지 않았을 수 있습니다.)`,
                '로컬: .env의 REACT_APP_API_BASE_URL(또는 DEV_PROXY_TARGET)이 GET /product/list 를 제공하는 백엔드를 가리키는지 확인하세요.',
                `상세: ${extractMessage(error)}`,
            ].join(' ');
            throw new ApiError(404, error.statusText, error.data, msg);
        }
        throw new Error(extractMessage(error) || '프로덕트 목록을 불러오는데 실패했습니다.');
    }
};

/** API 한 행 → PurchasedProductItemDto (camel/snake·중첩 productPrice 호환) */
function resolvePurchasedRowProductType(r: Record<string, unknown>): ProductType | undefined {
    const tryRaw = (v: unknown): ProductType | undefined => {
        const n = normalizeApiProductType(v);
        if (n) return n;
        if (typeof v !== 'string') return undefined;
        const u = v.trim();
        if (
            u === 'CONTENT' ||
            u === 'EQUIPMENT' ||
            u === 'CUSTOM_SERVICE' ||
            u === 'OPEN_CLASS' ||
            u === 'ORGANIZATION_CLASS'
        ) {
            return u as ProductType;
        }
        return undefined;
    };
    // API 문서 기준: type만 사용
    const t = tryRaw(r.type);
    if (t) return t;
    const prod = r.product;
    if (prod != null && typeof prod === 'object' && !Array.isArray(prod)) {
        const po = prod as Record<string, unknown>;
        const pt = tryRaw(po.type);
        if (pt) return pt;
    }
    return undefined;
}

/**
 * GET /product/organization/purchased 한 행 — 루트 + 중첩 `product`·주문라인 등에서
 * `type`·`purchaseType` 누락을 줄이기 위해, 없는 키만 루트에 펼쳐 합친 뷰.
 */
/** orderId·order_id 외 `order: { id }`·라인 항목 등에 붙는 주문 키 */
function extractOrderIdFromPurchasedRow(
	merged: Record<string, unknown>,
	raw: Record<string, unknown>
): number {
	// API 문서 기준: orderId만 사용
	const direct = Number(merged.orderId);
	if (Number.isFinite(direct) && direct > 0) return Math.floor(direct);
	const o = merged.order;
	if (o != null && typeof o === 'object' && !Array.isArray(o)) {
		const oo = o as Record<string, unknown>;
		// API 문서 기준: orderId만 사용
		const n = Number(oo.orderId);
		if (Number.isFinite(n) && n > 0) return Math.floor(n);
	}
	return 0;
}

function mergePurchasedRowShallowForLookup(r: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = { ...r };
    // API 문서 기준: product 중첩 객체만 확인
    const inner = r.product;
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
        for (const [k, v] of Object.entries(inner as Record<string, unknown>)) {
            if (out[k] === undefined) out[k] = v;
        }
    }
    return out;
}

function coercePurchaseTypeFromNested(r: Record<string, unknown>, root: Record<string, unknown>): ProductPricePurchaseType | undefined {
    const fromObj = (o: Record<string, unknown>): ProductPricePurchaseType | undefined => {
        // API 문서 기준: purchaseType만 사용
        const a = o.purchaseType;
        return coercePurchaseTypeEnum(a) ?? undefined;
    };
    const c1 = fromObj(r);
    if (c1) return c1;
    // API 문서 기준: productPrice만 사용
    const n = r.productPrice as unknown;
    if (n && typeof n === 'object' && !Array.isArray(n)) {
        const c2 = fromObj(n as Record<string, unknown>);
        if (c2) return c2;
    }
    return fromObj(root);
}

function normalizePurchasedProductRow(raw: unknown): PurchasedProductItemDto | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const r = raw as Record<string, unknown>;
    const merged = mergePurchasedRowShallowForLookup(r);
    // API 문서 기준: productId, orderId만 사용
    const productId = Number(merged.productId);
    if (!Number.isFinite(productId) || productId <= 0) return null;
    const orderIdRaw = Number(merged.orderId);
    const orderIdFromNested = extractOrderIdFromPurchasedRow(merged, r);
    const orderId =
    	Number.isFinite(orderIdRaw) && orderIdRaw > 0
    		? Math.floor(orderIdRaw)
    		: orderIdFromNested;
    // API 문서 기준: productPriceId만 사용
    const productPriceId = Number(merged.productPriceId);
    // API 문서 기준: productTitle, productPriceTitle만 사용
    const productTitle = String(merged.productTitle ?? '').trim();
    const productPriceTitle = String(merged.productPriceTitle ?? '').trim();
    const rowType = resolvePurchasedRowProductType(merged) ?? resolvePurchasedRowProductType(r);
    // startAt 처리
    const startAtRaw = merged.startAt ?? r.startAt;
    const startAt = startAtRaw instanceof Date ? startAtRaw : 
                      (typeof startAtRaw === 'string' || typeof startAtRaw === 'number') ? new Date(startAtRaw) : 
                      null;
    // expiresAt 처리
    const expiresAtRaw = merged.expiresAt ?? r.expiresAt;
    const expiresAt = expiresAtRaw instanceof Date ? expiresAtRaw : 
                      (typeof expiresAtRaw === 'string' || typeof expiresAtRaw === 'number') ? new Date(expiresAtRaw) : 
                      null;
    const out: PurchasedProductItemDto = {
        orderId: orderId > 0 ? orderId : 0,
        productId: Math.floor(productId),
        productTitle,
        productPriceId: Number.isFinite(productPriceId) && productPriceId > 0 ? Math.floor(productPriceId) : 0,
        productPriceTitle,
        startAt,
        expiresAt,
        ...(rowType ? { type: rowType } : {}),
    };
    // API 문서 기준: productPrice, purchaseType만 사용
    const nested = r.productPrice;
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
        out.productPrice = nested as PurchasedProductItemDto['productPrice'];
        const n = nested as Record<string, unknown>;
        const ptp = n.purchaseType;
        const coercedNested = coercePurchaseTypeEnum(ptp);
        if (coercedNested) {
            out.purchaseType = coercedNested;
        }
    }
    if (!out.purchaseType) {
        // API 문서 기준: purchaseType만 사용
        const coercedRoot = coercePurchaseTypeEnum(merged.purchaseType);
        if (coercedRoot) {
            out.purchaseType = coercedRoot;
        }
    }
    if (!out.purchaseType) {
        const c = coercePurchaseTypeFromNested(r, merged);
        if (c) {
            out.purchaseType = c;
        }
    }
    return out;
}

function normalizePurchasedListResponse(raw: unknown): GetPurchasedProductListResponseDto {
    if (!raw || typeof raw !== 'object') return { productList: [], totalCount: 0 };
    const o = raw as Record<string, unknown>;
    // API 문서 기준: productList, totalCount만 사용
    const rawList = Array.isArray(o.productList) ? o.productList : [];
    const list = rawList
        .map(normalizePurchasedProductRow)
        .filter((x): x is PurchasedProductItemDto => x != null);
    const totalCount =
        typeof o.totalCount === 'number'
            ? o.totalCount
            : 0;
    return { productList: list, totalCount };
}

export type MapPurchasedProductListOptions = {
	/** true: 기관클래스 폼용 — OPEN_CLASS·장비·커스텀 제외(CONTENT·ORGANIZATION_CLASS만). 타입 미상은 CONTENT 간주 */
	institutionClassProductSelect?: boolean;
};

/**
 * PurchasedProductItemDto → 클래스 폼 프로덕트 셀렉트용 (콘텐츠형만).
 * - 행에 type 이 있으면 사용, 없으면 `catalog`에서 productId 로 조회.
 * - 장비(EQUIPMENT)·커스텀(CUSTOM_SERVICE) 등은 제외.
 * - `institutionClassProductSelect`: 오픈클래스(OPEN_CLASS)·장비·커스텀 제외.
 */
export function mapPurchasedProductListToSelectItems(
    rows: PurchasedProductItemDto[],
    catalog?: ProductListItemDto[] | undefined,
    options?: MapPurchasedProductListOptions
): ProductListItemDto[] {
	const institutionOnly = options?.institutionClassProductSelect === true;
    const catalogById = new Map<number, ProductListItemDto>();
    for (const p of catalog ?? []) {
        if (p != null && typeof p.productId === 'number' && p.productId > 0) {
            catalogById.set(p.productId, p);
        }
    }
    const byProductPrice = new Map<number, ProductListItemDto>();
    const now = new Date().toISOString();
    const catalogEmpty = catalog == null || catalog.length === 0;

    for (const row of rows) {
        if (row == null || typeof row.productId !== 'number' || row.productId <= 0) continue;
        if (typeof row.productPriceId !== 'number' || row.productPriceId <= 0) continue;
        if (byProductPrice.has(row.productPriceId)) continue;

        const fromCatalog = catalogById.get(row.productId);
        /** 기관클래스 생성: GET /product/list 없이 purchased 만 쓸 때 type 이 없으면 CONTENT 로 간주(백엔드가 내려주는 것이 정석) */
        let effectiveType = row.type ?? fromCatalog?.type;
        /** 타입 미수신: 기관 폼에서도 카탈로그 없을 때 CONTENT 간주(API가 type 생략하는 환경 호환). OPEN_CLASS·EQUIPMENT 등 명시값만 아래에서 제외 */
        if (effectiveType == null && catalogEmpty) {
            effectiveType = 'CONTENT';
        }
        if (effectiveType == null) {
            continue;
        }
        const typeAllowed = institutionOnly
            ? isInstitutionClassProductSelectType(effectiveType)
            : isContentLikeProductType(effectiveType);
        if (!typeAllowed) {
            continue;
        }

        const productTitle = row.productTitle?.trim() || fromCatalog?.title || '';
        const priceTitle = row.productPriceTitle?.trim() || '';
        const displayTitle = priceTitle ? `${productTitle} (${priceTitle})` : productTitle;

        byProductPrice.set(row.productPriceId, {
            productId: row.productId,
            title: displayTitle,
            type: effectiveType,
            scenarioCount: fromCatalog?.scenarioCount ?? 0,
            lectureCount: fromCatalog?.lectureCount ?? 0,
            priceCount: fromCatalog?.priceCount ?? 0,
            isActive: fromCatalog?.isActive !== false,
            createdAt: fromCatalog?.createdAt ?? now,
            updatedAt: fromCatalog?.updatedAt ?? now,
            premiumTypes: fromCatalog?.premiumTypes ?? [],
        });
    }
    const mapped = [...byProductPrice.values()];
    return mapped;
}

/** GET /product/organization/purchased — page·pageSize 필수, 전 페이지 병합 */
export const getOrganizationPurchasedProducts = async (
    organizationId: number,
    language?: string
): Promise<GetPurchasedProductListResponseDto> => {
    try {
        const oid = Math.floor(Number(organizationId));
        if (!Number.isFinite(oid) || oid <= 0) {
            return { productList: [], totalCount: 0 };
        }
        const merged: PurchasedProductItemDto[] = [];
        let page = 1;
        let totalCount = 0;
        for (;;) {
            const params = {
                organizationId: oid,
                page,
                pageSize: PURCHASED_PRODUCT_PAGE_SIZE,
                ...(language ? { language } : {}),
            };
            const raw = await apiClient.get<unknown>(`/product/organization/purchased`, {
                params,
            });
            const chunk = normalizePurchasedListResponse(raw);
            totalCount = chunk.totalCount;
            merged.push(...chunk.productList);
            if (merged.length >= totalCount || chunk.productList.length === 0) break;
            page += 1;
            if (page > 500) break;
        }
        return { productList: merged, totalCount };
    } catch (error) {
        throw new Error(extractMessage(error) || '기관 구매 프로덕트 목록을 불러오는데 실패했습니다.');
    }
};

/**
 * GET /product/{productId} — **프로덕트 단건(기본정보·이수조건·prices·items 등)**.
 * 커리큘럼만 필요하면 `getProductItemList` → `GET /product/{id}/items` (이수 숫자 필드 없음).
 */
export const getProductDetail = async (
    productId: number,
    opts?: { signal?: AbortSignal; language?: string }
): Promise<GetProductDetailResponseDto> => {
    try {
        const id = Math.floor(Number(productId));
        if (!Number.isFinite(id) || id <= 0) {
            throw new Error('유효하지 않은 프로덕트 ID입니다.');
        }
        const raw = await apiClient.get<unknown>(`/product/${id}`, {
            signal: opts?.signal,
            params: opts?.language ? { language: opts.language } : {},
        });
        return normalizeGetProductDetailPayload(raw);
    } catch (error) {
        if (error instanceof ApiError) throw error;
        throw new Error(extractMessage(error) || '프로덕트 정보를 불러오는데 실패했습니다.');
    }
};

/** GET /product/{productId}/items — 커리큘럼 항목만 (상세 GET 권한과 별도일 수 있음) */
export const getProductItemList = async (
    productId: number,
    language?: string
): Promise<GetProductItemListResponseDto> => {
    try {
        const id = Math.floor(Number(productId));
        if (!Number.isFinite(id) || id <= 0) {
            return { itemList: [] };
        }
        return await apiClient.get<GetProductItemListResponseDto>(`/product/${id}/items`, {
            params: language ? { language } : {},
        });
    } catch (error) {
        throw new Error(extractMessage(error) || '프로덕트 커리큘럼을 불러오는데 실패했습니다.');
    }
};

async function fetchAllProductPriceListRows(language?: string): Promise<ProductPriceListItemDto[]> {
    const first = await getProductPriceList(
        { page: 1, pageSize: MAX_PRODUCT_PRICE_LIST_PAGE_SIZE },
        { language }
    );
    const merged = [...(first.list ?? [])];
    const totalPages = first.totalPages ?? 1;
    for (let page = 2; page <= totalPages && page <= 200; page++) {
        const next = await getProductPriceList(
            { page, pageSize: MAX_PRODUCT_PRICE_LIST_PAGE_SIZE },
            { language }
        );
        merged.push(...(next.list ?? []));
    }
    return merged;
}

function mapProductItemListRowToDetail(row: ProductItemListRowDto): ProductItemDetailDto {
    const raw = (row.type ?? '').toString().toUpperCase();
    const isLecture = raw === 'LECTURE';
    const deviceTypes = row.scenarioDeviceTypes;
    const deviceType = Array.isArray(deviceTypes) && deviceTypes.length > 0 
        ? deviceTypes.join(', ') 
        : undefined;
    
    return {
        order: row.order,
        itemType: isLecture ? 'LECTURE' : 'SCENARIO',
        title: (isLecture ? row.lectureTitle : row.scenarioTitle) || '',
        scenarioId: row.scenarioId,
        lectureId: row.lectureId,
        lectureTitle: row.lectureTitle,
        lectureVideoUrl: row.lectureVideoUrl,
        lectureDescription: row.lectureDescription,
        lectureCreatorName: row.lectureCreatorName,
        expectedPlayTimeSeconds: row.scenarioExpectedPlayTimeSeconds,
        ...(deviceType ? { deviceType } : {}),
        ...(deviceTypes ? { scenarioDeviceTypes: deviceTypes } : {}),
        ...(row.lectureList?.length ? { lectureList: row.lectureList } : {}),
    };
}

function coerceNonNegativeMoney(v: unknown): number | undefined {
	if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
	if (typeof v === 'string' && v.trim()) {
		const n = Number(v.replace(/,/g, '').trim());
		if (Number.isFinite(n) && n >= 0) return n;
	}
	return undefined;
}

/** GET /product/price/list 행 — 목록에 정가·할인가가 오면 사용, 없으면 unitPrice만 정가로(레거시) */
function mapPriceListRowToDetail(row: ProductPriceListItemDto): ProductPriceDetailDto {
	const ext = row as ProductPriceListItemDto & Record<string, unknown>;
	// API 문서 기준: regularPrice, discountedPrice만 사용
	const regularPrice =
		coerceNonNegativeMoney(ext.regularPrice) ??
		coerceNonNegativeMoney(row.unitPrice) ??
		0;
	const discountedPrice =
		coerceNonNegativeMoney(ext.discountedPrice) ??
		undefined;
	return {
		skuCode: row.skuCode,
		productPriceId: row.productPriceId,
		title: row.title,
		regularPrice,
		...(discountedPrice != null ? { discountedPrice } : {}),
		currency: row.currency,
		purchaseType: 'PER_USER',
		costPrice: row.costPrice ?? undefined,
		isActive: row.isActive,
		countryTitle: row.countryTitle ?? undefined,
	};
}

function coerceCurrencyFromUnknown(v: unknown): ProductPriceCurrency | undefined {
    if (v === 'KRW' || v === 'USD') return v;
    return undefined;
}

function nestedPurchasedPriceNumbers(nested: Record<string, unknown> | undefined): number {
    if (!nested) return 0;
    for (const key of ['regularPrice', 'unitPrice', 'discountedPrice'] as const) {
        const raw = nested[key];
        const n = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isFinite(n) && n >= 0) return n;
    }
    return 0;
}

/**
 * GET /product/organization/purchased 행만으로 판매단위 DTO를 만듭니다.
 * (GET /product/price/list 는 어드민 전용이므로 마스터·기관 클래스 폼에서는 사용하지 않음)
 */
function mapPurchasedRowToProductPriceDetail(row: PurchasedProductItemDto): ProductPriceDetailDto {
    const nested =
        row.productPrice != null && typeof row.productPrice === 'object' && !Array.isArray(row.productPrice)
            ? (row.productPrice as Record<string, unknown>)
            : undefined;
    const resolvedId = resolvePurchasedProductPriceId([row], row.productId, row.orderId);
    const productPriceId =
        resolvedId > 0 ? resolvedId : row.productPriceId > 0 ? row.productPriceId : 0;
    const titleFromNested =
        nested && nested.title != null && String(nested.title).trim() ? String(nested.title).trim() : '';
    const title = titleFromNested || row.productPriceTitle?.trim() || '';
    // API 문서 기준: currency만 사용
    const currency =
        coerceCurrencyFromUnknown(nested?.currency) ?? 'KRW';
    const regularPrice = nestedPurchasedPriceNumbers(nested);
    const purchaseType = row.purchaseType ?? 'PER_USER';
    return {
        skuCode: '',
        productPriceId,
        title,
        regularPrice,
        currency,
        purchaseType,
        isActive: true,
    };
}

export type ProductDetailForClassFormHint = {
    title?: string;
    type?: ProductType;
    /**
     * 기관 구매 프로덕트 행이 있으면 GET /product/{id}/items 만 호출하고,
     * 판매단위는 이 행(productPriceId 등)으로 조합합니다. GET /product/price/list 는 호출하지 않습니다.
     */
    purchasedPriceRow?: PurchasedProductItemDto;
};

/**
 * 클래스 생성·수정 폼용: GET /product/{id} 가 403일 때를 대비해
 * GET /product/{id}/items + (기본) 판매단위 목록 또는 (기관) 구매 행으로 상세 DTO를 조합합니다.
 */
export const getProductDetailForClassForm = async (
    productId: number,
    hint?: ProductDetailForClassFormHint,
    language?: string
): Promise<GetProductDetailResponseDto> => {
    const pid = Math.floor(Number(productId));
    if (!Number.isFinite(pid) || pid <= 0) {
        throw new Error('유효하지 않은 프로덕트입니다.');
    }

    const purchased = hint?.purchasedPriceRow;
    if (purchased && purchased.productId === pid) {
        const itemsRes = await getProductItemList(pid, language);
        const items = (itemsRes.itemList ?? []).map(mapProductItemListRowToDetail);
        const priceRow = mapPurchasedRowToProductPriceDetail(purchased);
        const title =
            (hint?.title && hint.title.trim()) || purchased.productTitle?.trim() || '';
        const type: ProductType = hint?.type ?? purchased.type ?? 'CONTENT';
        const now = new Date().toISOString();
        return {
            type,
            title,
            description: undefined,
            items,
            isActive: true,
            createdAt: now,
            prices: priceRow.productPriceId > 0 ? [priceRow] : [],
        };
    };

    const itemsRes = await getProductItemList(pid, language);
    const items = (itemsRes.itemList ?? []).map(mapProductItemListRowToDetail);

    let prices: ProductPriceDetailDto[] = [];
    let title = (hint?.title && hint.title.trim()) || '';
    let type: ProductType | undefined = hint?.type;

    try {
        const detail = await getProductDetail(pid, { language });
        if (Array.isArray(detail.prices) && detail.prices.length > 0) {
            prices = detail.prices;
        }
        if (!title) title = detail.title?.trim() ?? '';
        if (!type) type = detail.type;
    } catch (e) {
        if (!(e instanceof ApiError && (e.status === 401 || e.status === 403))) {
            throw e;
        }
    }

    if (prices.length === 0) {
        const priceRows = await fetchAllProductPriceListRows(language);
        const fromRow = priceRows.find((r) => r.productId === pid);
        if (!title) title = fromRow?.productTitle ?? '';
        if (!type) type = fromRow?.productType ?? 'CONTENT';
        prices = priceRows.filter((r) => r.productId === pid).map(mapPriceListRowToDetail);
    }

    const now = new Date().toISOString();
    return {
        type: type ?? 'CONTENT',
        title,
        description: undefined,
        items,
        isActive: true,
        createdAt: now,
        prices,
    };
};

/** POST /product — items·translations 를 OpenAPI(`CreateProductItemDto`, `ProductTranslationDto`) 형태로 정규화 */
export const createProduct = async (data: CreateProductRequestDto): Promise<CreateProductResponseDto> => {
    try {
        const body = normalizeCreateProductPayload(data);
        const result = await apiClient.post<CreateProductResponseDto>(`/product`, body);
        logResponseData(result);
        return result;
    } catch (error) {
        logErrorResponseData(error);
        throw new Error(extractMessage(error) || '프로덕트 생성에 실패했습니다.');
    }
};

/** PUT /product/{productId} — i18n: 루트 title/description 없음, translations·lectureList */
export const updateProduct = async (
    productId: number,
    data: UpdateProductRequestDto
): Promise<UpdateProductResponseDto> => {
    try {
        const body = normalizeUpdateProductPayload(data);
        return await apiClient.put<UpdateProductResponseDto>(`/product/${productId}`, body);
    } catch (error) {
        throw new Error(extractMessage(error) || '프로덕트 수정에 실패했습니다.');
    }
};

/** DELETE /product/{productId} */
export const deleteProduct = async (productId: number): Promise<DeleteProductResponseDto> => {
    try {
        return await apiClient.delete<DeleteProductResponseDto>(`/product/${productId}`);
    } catch (error) {
        throw new Error(extractMessage(error) || '프로덕트 삭제에 실패했습니다.');
    }
};

/** POST /product/price */
export const createProductPrice = async (
    data: CreateProductPriceRequestDto
): Promise<CreateProductPriceResponseDto> => {
    try {
        return await apiClient.post<CreateProductPriceResponseDto>(`/product/price`, data);
    } catch (error) {
        throw new Error(extractMessage(error) || '판매단위 생성에 실패했습니다.');
    }
};

/** `GET /product/price/list` 쿼리 (OpenAPI: page·pageSize 필수, productType·isActive·searchText·language 선택) */
export type GetProductPriceListQueryParams = {
    page?: number;
    pageSize?: number;
    searchText?: string;
    /** OpenAPI enum: CONTENT | EQUIPMENT | CUSTOM_SERVICE */
    productType?: 'CONTENT' | 'EQUIPMENT' | 'CUSTOM_SERVICE';
    isActive?: boolean;
};

/** GET /product/price/list — **판매단위(가격) 행** 목록 (프로덕트 목록은 `GET /product/list` / `getProductList`). */
export const getProductPriceList = async (
    params?: GetProductPriceListQueryParams,
    opts?: { signal?: AbortSignal; language?: string }
): Promise<GetProductPriceListResponseDto> => {
    try {
        const normalizedPage =
            typeof params?.page === 'number' && Number.isFinite(params.page) && params.page > 0
                ? Math.floor(params.page)
                : 1;
        const normalizedPageSize =
            typeof params?.pageSize === 'number' && Number.isFinite(params.pageSize)
                ? Math.max(1, Math.min(MAX_PRODUCT_PRICE_LIST_PAGE_SIZE, Math.floor(params.pageSize)))
                : 20;
        const normalizedSearchText = params?.searchText?.trim();
        const query: Record<string, string | number | boolean> = {
            page: normalizedPage,
            pageSize: normalizedPageSize,
        };
        if (normalizedSearchText) {
            query.searchText = normalizedSearchText;
        }
        if (params?.productType) {
            query.productType = params.productType;
        }
        if (params?.isActive !== undefined) {
            query.isActive = params.isActive;
        }
        if (opts?.language) {
            query.language = opts.language;
        }

        const raw = await apiClient.get<unknown>(`/product/price/list`, {
            params: query,
            signal: opts?.signal,
        });
        return normalizeGetProductPriceListPayload(raw);
    } catch (error) {
        throw new Error(extractMessage(error) || '판매단위 목록을 불러오는데 실패했습니다.');
    }
};

/** 클래스 상세에 productId가 없을 때 등 — 판매단위 ID로 목록에서 한 행 조회 */
export async function findProductPriceListRowByProductPriceId(
    productPriceId: number,
    opts?: { signal?: AbortSignal; language?: string }
): Promise<ProductPriceListItemDto | null> {
    const target = Math.floor(Number(productPriceId));
    if (!Number.isFinite(target) || target <= 0) return null;
    try {
        const first = await getProductPriceList(
            { page: 1, pageSize: MAX_PRODUCT_PRICE_LIST_PAGE_SIZE },
            { signal: opts?.signal, language: opts?.language }
        );
        const hit = (first.list ?? []).find((r) => r.productPriceId === target);
        if (hit) return hit;
        const totalPages = first.totalPages ?? 1;
        for (let page = 2; page <= totalPages && page <= 200; page++) {
            const next = await getProductPriceList(
                { page, pageSize: MAX_PRODUCT_PRICE_LIST_PAGE_SIZE },
                { signal: opts?.signal, language: opts?.language }
            );
            const h = (next.list ?? []).find((r) => r.productPriceId === target);
            if (h) return h;
        }
        return null;
    } catch {
        return null;
    }
}

/** DELETE /product/price/{productPriceId} */
export const deleteProductPrice = async (
    productPriceId: number
): Promise<DeleteProductPriceResponseDto> => {
    try {
        return await apiClient.delete<DeleteProductPriceResponseDto>(
            `/product/price/${productPriceId}`
        );
    } catch (error) {
        throw new Error(extractMessage(error) || '판매단위 삭제에 실패했습니다.');
    }
};
